<script setup>
import { ref, onMounted } from "vue";
import AppDataTable from "@/components/AppDataTable.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { formatDate, toDate } from "@/lib/date";
import { fetchRecognitionStamps, deleteStamp, archiveStamps, exportStampsUrl } from "./api";

const columns = [
  { key: "author",           label: "Author",           sortable: true },
  { key: "authorCompany",    label: "Author Company" },
  { key: "recipient",        label: "Recipient",        sortable: true },
  { key: "recipientCompany", label: "Recipient Company" },
  { key: "alfStamp",         label: "ALF Stamp" },
  { key: "content",          label: "Content" },
  { key: "createdDate",      label: "Created Date" },
  { key: "actions",          label: "Actions",          align: "end", width: "120px" },
];
const rows = ref([]);
const loading = ref(false);
const error = ref("");
const pendingDelete = ref(null);
const deleting = ref(false);
const archiving = ref(false);

async function load() {
  loading.value = true; error.value = "";
  try {
    const list = await fetchRecognitionStamps();
    rows.value = list
      .map((s) => ({
        id: s.id ?? s._id,
        author: s.awarderName ?? s.awarderActualName ?? "—",
        authorCompany: s.awarderCompany ?? "—",
        recipient: s.recipientName ?? s.recipientActualName ?? "—",
        recipientCompany: s.recipientCompany ?? "—",
        alfStamp: s.stampAwarded ?? "—",
        content: (s.content ?? "").slice(0, 60),
        createdDate: formatDate(s.createdAt) || "—",
        _sortDate: toDate(s.createdAt)?.getTime() ?? 0,
      }))
      .sort((a, b) => b._sortDate - a._sortDate);
  }
  catch { error.value = "Couldn't load stamps."; }
  finally { loading.value = false; }
}

async function confirmDelete() {
  if (!pendingDelete.value) return;
  deleting.value = true;
  try { await deleteStamp(pendingDelete.value.id); rows.value = rows.value.filter((r) => r.id !== pendingDelete.value.id); pendingDelete.value = null; }
  catch { error.value = "Couldn't delete."; }
  finally { deleting.value = false; }
}

async function archive() {
  archiving.value = true;
  try { await archiveStamps(); await load(); }
  catch { error.value = "Couldn't archive stamps."; }
  finally { archiving.value = false; }
}

onMounted(load);
</script>

<template>
  <section>
    <div class="d-flex align-center justify-space-between mb-4 gap-3 flex-wrap">
      <h1 class="text-h5">Recognition Stamps</h1>
      <div class="d-flex gap-2">
        <VBtn variant="tonal" @click="load">Refresh</VBtn>
        <VBtn variant="tonal" :loading="archiving" @click="archive">Archive All</VBtn>
        <VBtn variant="tonal" :href="exportStampsUrl()" target="_blank">Export CSV</VBtn>
      </div>
    </div>
    <VAlert v-if="error" type="error" closable class="mb-4" @click:close="error = ''">{{ error }}</VAlert>
    <AppDataTable :columns="columns" :rows="rows" :loading="loading" row-key="id" searchable search-placeholder="Search stamps…" empty-text="No recognition stamps.">
      <template #cell-actions="{ row }">
        <VBtn size="small" color="error" variant="text" @click.stop="pendingDelete = row">Delete</VBtn>
      </template>
    </AppDataTable>
    <ConfirmDialog :model-value="pendingDelete !== null" title="Delete stamp?" message="Permanently delete this stamp?" :loading="deleting" @update:model-value="pendingDelete = null" @confirm="confirmDelete" />
  </section>
</template>
